import React, { useState } from 'react';
import { Empty, Spin, Alert, Button, Space, Popconfirm, Select, Switch, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DndContext, DragCancelEvent, DragEndEvent, DragOverEvent, DragStartEvent, DragOverlay, useDroppable, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { getTasks, updateTask, deleteTask, Task, PlannerPage } from '../../../services/tasks';
import TaskCard from './TaskCard';
import { useWebSocket } from '../../../hooks/useWebSocket';
import { colors } from '../../../theme/colors';
import './PlannerViews.css';
import { taskReference } from '../../../utils/publicReferences';

interface BoardViewProps {
  plannerPage: PlannerPage;
  onTaskOpen?: (taskId: string) => void;
  onCreateTask?: (defaults: { status: string; swimlane?: string }) => void;
  filters?: {
    search?: string;
    assigneeId?: string;
    priority?: string;
    tags?: string[];
    status?: string;
  };
}

const DROP_ID_SEP = '::';

const DroppableColumn: React.FC<{ 
  status: string; 
  swimlaneKey?: string; // when in swimlane mode, unique id = swimlaneKey::status
  taskCount: number;
  wipLimit?: number;
  compact?: boolean;
  children: React.ReactNode;
}> = ({ status, swimlaneKey, taskCount, wipLimit, compact = false, children }) => {
  const dropId = swimlaneKey !== undefined ? `${swimlaneKey}${DROP_ID_SEP}${status}` : status;
  const { setNodeRef, isOver } = useDroppable({
    id: dropId,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        minWidth: compact ? 0 : '320px',
        width: compact ? 'auto' : '320px',
        minHeight: compact ? 96 : undefined,
        background: isOver ? 'rgba(219, 80, 172, 0.08)' : 'transparent',
        padding: compact ? '10px' : '16px',
        display: 'flex',
        flexDirection: 'column',
        transition: 'background 0.18s ease',
      }}
    >
      {children}
    </div>
  );
};

const BoardView: React.FC<BoardViewProps> = ({
  plannerPage,
  filters = {},
  onTaskOpen,
  onCreateTask,
}) => {
  const queryClient = useQueryClient();
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [multiselectColumn, setMultiselectColumn] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showEmptySwimlanes, setShowEmptySwimlanes] = useState(true);
  const [dragOverStatus, setDragOverStatus] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Start drag after 8px movement - feels instant but prevents accidental drags
      },
    })
  );
  
  // Connect to WebSocket for real-time updates
  useWebSocket({ plannerPageId: plannerPage.id, enabled: true });

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['tasks', plannerPage.id, filters],
    queryFn: () => getTasks({ 
      plannerPageId: plannerPage.id,
      ...filters,
    }),
  });

  const updateTaskMutation = useMutation({
    mutationFn: updateTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', plannerPage.id] });
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: async (taskIds: string[]) => {
      await Promise.all(taskIds.map((taskId) => deleteTask(taskId)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', plannerPage.id] });
      message.success('Tasks deleted successfully');
      setSelectedTasks(new Set());
      setMultiSelectMode(false);
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ taskIds, updates }: { taskIds: string[]; updates: any }) => {
      await Promise.all(taskIds.map(id => updateTask({ id, ...updates })));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', plannerPage.id] });
      message.success('Tasks updated successfully');
      setSelectedTasks(new Set());
      setMultiSelectMode(false);
    },
  });

  const handleTaskClick = (task: Task, status: string, e: React.MouseEvent) => {
    // If dragging, don't open modal
    if (isDragging) {
      return;
    }

    // If Shift is pressed, do multiselect
    if (e.shiftKey) {
      // If multiselect hasn't started, start it in this column
      if (!multiSelectMode) {
        setMultiselectColumn(status);
        setSelectedTasks(new Set([task.id]));
        setMultiSelectMode(true);
      } else {
        // Only allow multiselect within the same column
        if (multiselectColumn === status) {
          const newSelected = new Set(selectedTasks);
          if (newSelected.has(task.id)) {
            newSelected.delete(task.id);
            // If no tasks selected, exit multiselect mode
            if (newSelected.size === 0) {
              setMultiSelectMode(false);
              setMultiselectColumn(null);
            }
          } else {
            newSelected.add(task.id);
          }
          setSelectedTasks(newSelected);
        }
      }
    } else {
      // Normal click - open modal or clear selection
      if (multiSelectMode) {
        // Clear selection if clicking without shift
        setSelectedTasks(new Set());
        setMultiSelectMode(false);
        setMultiselectColumn(null);
      } else {
        // Open task details
        onTaskOpen?.(taskReference(task));
      }
    }
  };

  const handleBulkDelete = () => {
    const taskIds = Array.from(selectedTasks);
    deleteTaskMutation.mutate(taskIds);
  };

  const handleBulkMove = (newStatus: string) => {
    const taskIds = Array.from(selectedTasks);
    bulkUpdateMutation.mutate({ taskIds, updates: { status: newStatus } });
  };

  const handleDragStart = (event: DragStartEvent) => {
    const task = event.active.data.current?.task as Task;
    setActiveTask(task);
    setIsDragging(true);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const overId = event.over ? String(event.over.id) : '';
    setDragOverStatus(overId ? overId.split(DROP_ID_SEP).pop() || null : null);
  };

  const handleDragCancel = (_event: DragCancelEvent) => {
    setActiveTask(null);
    setIsDragging(false);
    setDragOverStatus(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);
    setIsDragging(false);
    setDragOverStatus(null);

    if (!over) return;

    const taskId = active.id as string;
    const overId = String(over.id);
    const task = active.data.current?.task as Task;

    let newStatus: string;
    let newSwimlane: string | undefined;
    if (hasSwimlanes && overId.includes(DROP_ID_SEP)) {
      const [parsedSwimlane, parsedStatus] = overId.split(DROP_ID_SEP);
      newStatus = parsedStatus;
      newSwimlane = parsedSwimlane === 'unassigned' ? undefined : parsedSwimlane;
    } else {
      newStatus = overId;
      newSwimlane = undefined;
    }

    const statusChanged = task && task.status !== newStatus;
    const swimlaneChanged = hasSwimlanes && task && (task.swimlane || 'unassigned') !== (newSwimlane || 'unassigned');
    if (task && (statusChanged || swimlaneChanged)) {
      const wipLimits = (plannerPage as any).wipLimits || {};
      const wipLimit = wipLimits[newStatus];
      const tasksInTargetStatus = tasks.filter((t) => t.status === newStatus && t.id !== taskId);
      if (statusChanged && wipLimit !== undefined && tasksInTargetStatus.length >= wipLimit) {
        message.warning(`WIP limit reached for ${newStatus}`);
        return;
      }
      updateTaskMutation.mutate({
        id: taskId,
        status: newStatus,
        ...(hasSwimlanes ? { swimlane: newSwimlane || '' } : {}),
      });
    }
  };

  const tasksByStatus = plannerPage.statuses.reduce((acc, status) => {
    acc[status] = tasks.filter((task) => task.status === status);
    return acc;
  }, {} as Record<string, Task[]>);

  const wipLimits = (plannerPage as any).wipLimits || {};
  const swimlanes = React.useMemo(() => (plannerPage as any).swimlanes || [], [plannerPage]);
  const hasSwimlanes = swimlanes.length > 0;

  // For swimlane layout: get tasks by swimlane and status (each row = one swimlane, full columns)
  const swimlaneKeysOrder = React.useMemo(
    () => (hasSwimlanes ? ['unassigned', ...swimlanes.map((s: any) => `${s.type}:${s.value}`)] : []),
    [hasSwimlanes, swimlanes]
  );
  const tasksBySwimlaneAndStatus = React.useMemo(() => {
    const map: Record<string, Record<string, Task[]>> = {};
    swimlaneKeysOrder.forEach((key) => {
      map[key] = {};
      plannerPage.statuses.forEach((status) => {
        map[key][status] = tasks.filter(
          (t) => (t.swimlane || 'unassigned') === key && t.status === status
        );
      });
    });
    return map;
  }, [tasks, swimlaneKeysOrder, plannerPage.statuses]);

  const getSwimlaneLabel = (key: string) => {
    if (key === 'unassigned') return 'Unassigned';
    const s = swimlanes.find((x: any) => `${x.type}:${x.value}` === key);
    return s ? `${s.type}: ${s.value}` : key;
  };

  const swimlaneTaskCount = (key: string) =>
    plannerPage.statuses.reduce(
      (count, status) => count + (tasksBySwimlaneAndStatus[key]?.[status]?.length || 0),
      0
    );

  const populatedSwimlaneKeys = swimlaneKeysOrder.filter((key) => swimlaneTaskCount(key) > 0);
  const visibleSwimlaneKeys = showEmptySwimlanes
    ? swimlaneKeysOrder
    : populatedSwimlaneKeys;
  const swimlaneGridColumns = `180px repeat(${plannerPage.statuses.length}, minmax(280px, 1fr))`;
  const swimlaneStatusColumns = `repeat(${plannerPage.statuses.length}, minmax(280px, 1fr))`;
  const swimlaneGridMinWidth = 180 + plannerPage.statuses.length * 292;

  if (isLoading) {
    return <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />;
  }

  return (
    <>
      {/* Bulk Actions Bar */}
      {multiSelectMode && selectedTasks.size > 0 && (
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: colors.bgBase,
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 16,
          border: `1px solid ${colors.primary}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{ color: colors.textPrimary, fontWeight: 600 }}>
            {selectedTasks.size} task{selectedTasks.size > 1 ? 's' : ''} selected
          </div>
          <Space>
            <Select
              size="small"
              aria-label="Move selected tasks"
              placeholder="Move selected…"
              options={plannerPage.statuses.map((status) => ({ label: status, value: status }))}
              onChange={handleBulkMove}
              style={{ width: 160 }}
            />
            <Popconfirm
              title={`Delete ${selectedTasks.size} task(s)?`}
              onConfirm={handleBulkDelete}
              okText="Yes"
              cancelText="No"
            >
              <Button
                size="small"
                danger
                icon={<FontAwesomeIcon icon={faTrash} />}
                loading={deleteTaskMutation.isPending}
              >
                Delete
              </Button>
            </Popconfirm>
            <Button
              size="small"
              icon={<FontAwesomeIcon icon={faXmark} />}
              onClick={() => {
                setSelectedTasks(new Set());
                setMultiSelectMode(false);
              }}
            >
              Cancel
            </Button>
          </Space>
        </div>
      )}

      {/* Multi-select toggle */}
      {multiSelectMode && selectedTasks.size > 0 && (
        <div style={{ marginBottom: 16, padding: '8px 12px', background: colors.bgBase, borderRadius: 8, border: `1px solid ${colors.border}` }}>
          <span style={{ color: colors.textMuted, fontSize: 12 }}>
            Hold Shift and click tasks in the same column to select multiple
          </span>
        </div>
      )}

      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {hasSwimlanes ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ color: colors.textMuted, fontSize: 13 }}>Show all swimlanes</span>
              <Switch
                size="small"
                checked={showEmptySwimlanes}
                onChange={setShowEmptySwimlanes}
                aria-label="Show all swimlanes"
              />
            </div>
            <div
              className="planner-swimlane-scroll"
              role="region"
              aria-label="Planner board"
              tabIndex={0}
            >
              <div style={{ minWidth: swimlaneGridMinWidth }}>
                <div
                  className="planner-swimlane-header"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: swimlaneGridColumns,
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      color: colors.textMuted,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      alignSelf: 'stretch',
                      display: 'flex',
                      alignItems: 'center',
                      position: 'sticky',
                      left: 0,
                      zIndex: 12,
                      background: colors.bgBase,
                      borderRight: `1px solid ${colors.border}`,
                    }}
                  >
                    Swimlane
                  </div>
                  {plannerPage.statuses.map((status) => {
                    const taskCount = tasksByStatus[status]?.length || 0;
                    const wipLimit = wipLimits[status];
                    const atLimit = wipLimit !== undefined && taskCount >= wipLimit;
                    return (
                      <div
                        key={status}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 10px',
                          borderBottom: `${dragOverStatus === status ? 2 : 1}px solid ${dragOverStatus === status ? colors.primary : colors.border}`,
                          transition: 'border-color 0.16s ease, border-width 0.16s ease',
                        }}
                      >
                        <span style={{ color: colors.textPrimary, fontSize: 13, fontWeight: 700, textTransform: 'capitalize' }}>{status}</span>
                        <span style={{ color: atLimit ? colors.error : colors.textMuted, fontSize: 12 }}>
                          {taskCount}{wipLimit !== undefined ? ` / ${wipLimit}` : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {visibleSwimlaneKeys.map((swimlaneKey) => {
              const tasksForSwimlane = tasksBySwimlaneAndStatus[swimlaneKey];
              const laneTaskCount = swimlaneTaskCount(swimlaneKey);
              return (
                <div
                  key={swimlaneKey}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: swimlaneGridColumns,
                    gap: 12,
                    padding: '12px 0',
                    borderTop: `1px solid ${colors.border}`,
                    alignItems: 'start',
                  }}
                >
                  <div
                    style={{
                      padding: '10px 18px 10px 0',
                      position: 'sticky',
                      left: 0,
                      zIndex: 6,
                      alignSelf: 'stretch',
                      minHeight: 96,
                      background: colors.bgBase,
                      borderRight: `1px solid ${colors.border}`,
                    }}
                  >
                    <div style={{ color: colors.textPrimary, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
                      {getSwimlaneLabel(swimlaneKey)}
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: 12 }}>
                      {laneTaskCount} task{laneTaskCount === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div
                    className="planner-swimlane-body"
                    style={{
                      gridColumn: '2 / -1',
                      display: 'grid',
                      gridTemplateColumns: swimlaneStatusColumns,
                      gap: 12,
                      alignItems: 'start',
                    }}
                  >
                      {plannerPage.statuses.map((status) => {
                        const columnTasks = tasksForSwimlane?.[status] || [];
                        const wipLimit = wipLimits[status];
                        return (
                          <DroppableColumn
                            key={status}
                            status={status}
                            swimlaneKey={swimlaneKey}
                            taskCount={columnTasks.length}
                            wipLimit={wipLimit}
                            compact
                          >
                            <div
                              className="planner-swimlane-column-scroll"
                              role="region"
                              aria-label={`${getSwimlaneLabel(swimlaneKey)}, ${status} tasks`}
                              tabIndex={0}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 10,
                                flex: 1,
                              }}
                            >
                              {columnTasks.length > 0 ? (
                                columnTasks.map((task) => (
                                  <TaskCard
                                    key={task.id}
                                    task={task}
                                    selected={selectedTasks.has(task.id)}
                                    onClick={(e) => handleTaskClick(task, status, e)}
                                  />
                                ))
                              ) : (
                                <div style={{ minHeight: 72, display: 'grid', placeItems: 'center' }}>
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<FontAwesomeIcon icon={faPlus} />}
                                    onClick={() => onCreateTask?.({
                                      status,
                                      swimlane: swimlaneKey === 'unassigned' ? '' : swimlaneKey,
                                    })}
                                    style={{ color: colors.textMuted }}
                                  >
                                    Create task
                                  </Button>
                                </div>
                              )}
                            </div>
                          </DroppableColumn>
                        );
                      })}
                    </div>
                  </div>
              );
                })}
              </div>
            </div>
          </div>
        ) : (
          // No swimlanes: single row of columns (original layout)
      <div
        className="planner-board-scroll planner-hide-scrollbar"
        role="region"
        aria-label="Planner board"
        tabIndex={0}
        style={{ display: 'flex', gap: '16px', overflowX: 'auto', paddingBottom: '16px', position: 'relative' }}
      >
          {plannerPage.statuses.map((status) => {
            const taskCount = tasksByStatus[status]?.length || 0;
            const wipLimit = wipLimits[status];
            const isOverLimit = wipLimit !== undefined && taskCount >= wipLimit;
            const isNearLimit = wipLimit !== undefined && taskCount >= wipLimit * 0.8;

            return (
              <DroppableColumn 
                key={status} 
                status={status}
                taskCount={taskCount}
                wipLimit={wipLimit}
              >
                <div
                  className="planner-board-column-header"
                  style={{
                    marginBottom: '16px',
                    paddingBottom: 10,
                    borderBottom: `${dragOverStatus === status ? 2 : 1}px solid ${dragOverStatus === status ? colors.primary : colors.border}`,
                    transition: 'border-color 0.16s ease, border-width 0.16s ease',
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: 8,
                  }}>
                    <h3 style={{ 
                      color: colors.textPrimary, 
                      margin: 0,
                      textTransform: 'capitalize',
                      fontSize: 14,
                      fontWeight: 600,
                    }}>
                      {status}
            </h3>
                    <div style={{ 
                      fontSize: 12, 
                      color: isOverLimit ? colors.error : isNearLimit ? '#FF9800' : colors.textMuted,
                      fontWeight: 500,
                    }}>
                      {taskCount}{wipLimit !== undefined ? ` / ${wipLimit}` : ''}
                    </div>
                  </div>
                  {isOverLimit && (
                    <Alert
                      message="WIP Limit Reached"
                      type="error"
                      showIcon
                      style={{ marginBottom: 8, fontSize: 11 }}
                    />
                  )}
                  {isNearLimit && !isOverLimit && (
                    <Alert
                      message="Approaching WIP Limit"
                      type="warning"
                      showIcon
                      style={{ marginBottom: 8, fontSize: 11 }}
                    />
                  )}
                </div>
                <div
                  className="planner-column-scroll"
                  role="region"
                  aria-label={`${status} tasks`}
                  tabIndex={0}
                  style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '12px', 
                    flex: 1,
                    position: 'relative',
                  }}
                >
                  {tasksByStatus[status]?.length > 0 ? (
                tasksByStatus[status].map((task) => (
                        <TaskCard 
                          key={task.id} 
                          task={task}
                          selected={selectedTasks.has(task.id)}
                          onClick={(e) => handleTaskClick(task, status, e)}
                        />
                ))
              ) : (
                <Empty
                  description="No tasks"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                        style={{ padding: '20px 0', color: colors.textMuted }}
                />
              )}
            </div>
          </DroppableColumn>
            );
          })}
      </div>
        )}
      <DragOverlay
        style={{
          cursor: 'grabbing',
        }}
        dropAnimation={null}
      >
        {activeTask ? (
          <div style={{
            width: '320px',
            minHeight: '120px',
            background: '#09090A',
            borderRadius: 10,
            padding: '14px',
            border: `1px solid ${colors.primary}`,
            pointerEvents: 'none',
          }}>
            <div style={{ color: colors.textPrimary, fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
              {activeTask.title}
            </div>
            {activeTask.description && (
              <div style={{ color: colors.textMuted, fontSize: 12, marginBottom: 8 }}>
                {activeTask.description.substring(0, 50)}...
              </div>
            )}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>

    </>
  );
};

export default BoardView;
